/**
 * Access Settings API Contracts — `GET`/`PATCH /v1/system/user-status-configs`
 *
 * This is the endpoint behind the **Access** tab of `/school/manage/settings`.
 * It carries two unrelated kinds of setting side by side, and the split is the
 * whole point of this contract:
 *
 * 1. `system` / `school` / `effective` — per-role default account status
 *    (`FACULTY -> active`, …). These are `UserRole -> UserStatusValue` maps.
 * 2. `platformManagerAutoActivate` / `platformManagerAutoAdmin` — two boolean
 *    switches governing what a Blackbaud **Platform Manager** gets for free at
 *    login: account activation, and School Journey app admin.
 *
 * The switches are top-level SIBLINGS of the role maps, never entries inside
 * them: those maps are keyed by role and valued by status, and structurally
 * cannot carry a boolean.
 *
 * ## Absent means ENABLED — read `effective`, never `school`
 *
 * Both switches default ON, and no row is seeded for any school. A school that
 * has never opted out therefore carries **no row at any level**, so
 * `school: null` and `system: null` are the normal case and mean *inherited*,
 * not *off*. The server resolves `effective` (school override → system row →
 * default ON) precisely so no client has to reimplement that rule.
 *
 * **Render `effective`. Use `school !== null` only to show "this school has
 * overridden the default."**
 *
 * ## Writes: omitted means "leave it alone"
 *
 * On PATCH, `configurations` and both switches are all optional, but the body
 * must contain at least one of them — an entirely empty body is rejected.
 * Omitting a switch leaves it untouched; it is NOT coerced to its default. That
 * matters because the default is ON: coercing would turn every unrelated
 * role-status save into a silent re-grant of app admin.
 *
 * `schoolId` is REQUIRED on both verbs. It used to be optional, and omitting it
 * targeted the system-wide row every school inherits.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-07-28-001-feat-platform-manager-auto-admin-override-plan.md (U5).
 */

import { z } from 'zod'

/** Roles the per-role status defaults are keyed by. */
export const UserStatusRoleSchema = z.enum(['FACULTY', 'STAFF', 'PARENT', 'STUDENT', 'CANDIDATE', 'OTHER'])

/** Account statuses a role default can resolve to. Lowercase, as stored. */
export const UserStatusValueSchema = z.enum([
  'active',
  'pending',
  'inactive',
  'suspended',
  'blocked',
  'deleted',
  'platform_only'
])

/** A partial role → status map. Absent roles fall back to `pending`. */
export const UserStatusConfigMapSchema = z.record(UserStatusRoleSchema, UserStatusValueSchema)

/**
 * One Platform Manager switch at all three levels.
 *
 * `system` and `school` are `null` when no row exists at that level — the
 * normal case. `effective` is never null and is what login actually acts on.
 */
export const PlatformManagerSwitchStateSchema = z.object({
  system: z.boolean().nullable(),
  school: z.boolean().nullable(),
  effective: z.boolean()
})

/** The two switch field names, as `updatedSwitches` reports them. */
export const PlatformManagerSwitchNameSchema = z.enum(['platformManagerAutoActivate', 'platformManagerAutoAdmin'])

/**
 * `GET /v1/system/user-status-configs?schoolId=…`
 */
export const GetUserStatusConfigsRequestSchema = z.object({
  schoolId: z.string().min(1)
})

export const GetUserStatusConfigsDataSchema = z.object({
  system: UserStatusConfigMapSchema,
  school: UserStatusConfigMapSchema.optional(),
  effective: UserStatusConfigMapSchema,
  /** Auto-ACTIVATE Blackbaud Platform Managers at login. */
  platformManagerAutoActivate: PlatformManagerSwitchStateSchema,
  /** Auto-grant School Journey app admin to Blackbaud Platform Managers. */
  platformManagerAutoAdmin: PlatformManagerSwitchStateSchema
})

export const GetUserStatusConfigsResponseSchema = z.object({
  data: GetUserStatusConfigsDataSchema,
  message: z.string()
})

/**
 * `PATCH /v1/system/user-status-configs?schoolId=…`
 *
 * At least one of `configurations` (non-empty), `platformManagerAutoActivate`,
 * or `platformManagerAutoAdmin` must be present.
 */
export const UpdateUserStatusConfigsRequestSchema = z.object({
  schoolId: z.string().min(1)
})

export const UpdateUserStatusConfigsBodySchema = z
  .object({
    configurations: UserStatusConfigMapSchema.optional(),
    platformManagerAutoActivate: z.boolean().optional(),
    platformManagerAutoAdmin: z.boolean().optional()
  })
  .refine(
    data =>
      Object.keys(data.configurations ?? {}).length > 0 ||
      data.platformManagerAutoActivate !== undefined ||
      data.platformManagerAutoAdmin !== undefined,
    'At least one configuration or Platform Manager switch must be provided'
  )

export const UpdateUserStatusConfigsResponseSchema = z.object({
  data: z.object({
    success: z.boolean(),
    updated: z.boolean(),
    updatedRoles: z.array(UserStatusRoleSchema),
    /** Switches this request wrote. Empty when the body carried none. */
    updatedSwitches: z.array(PlatformManagerSwitchNameSchema)
  }),
  message: z.string()
})

/**
 * The roles with a "Default status by role" switch on the Access tab, and so
 * the roles whose waiting users can be activated in bulk (SCH-3140).
 */
export const WaitingUserRoleSchema = z.enum(['FACULTY', 'STAFF', 'PARENT', 'STUDENT'])

/**
 * `GET /v1/system/user-status-configs/waiting-counts?schoolId=…`
 *
 * Users waiting for approval — PENDING with a linked account — counted by
 * PRIMARY role (faculty → staff → parent → student), so a faculty member who is
 * also a parent counts once, under FACULTY. The Access tab reads it to offer
 * activating them when an admin turns a role's switch on.
 */
export const GetWaitingUserCountsRequestSchema = z.object({
  schoolId: z.string().min(1)
})

export const WaitingUserCountsSchema = z.object({
  FACULTY: z.number().int().nonnegative(),
  STAFF: z.number().int().nonnegative(),
  PARENT: z.number().int().nonnegative(),
  STUDENT: z.number().int().nonnegative()
})

export const GetWaitingUserCountsResponseSchema = z.object({
  data: z.object({ counts: WaitingUserCountsSchema }),
  message: z.string()
})

/**
 * `POST /v1/system/user-status-configs/activate-waiting?schoolId=…`
 *
 * Activates every waiting user of one role, the same set the counts report.
 * `activatedCount` is counted by the server from the rows it wrote, so it can
 * be lower than the count shown before (someone was approved in between).
 */
export const ActivateWaitingUsersRequestSchema = z.object({
  schoolId: z.string().min(1)
})

export const ActivateWaitingUsersBodySchema = z.object({
  role: WaitingUserRoleSchema
})

export const ActivateWaitingUsersResponseSchema = z.object({
  data: z.object({ activatedCount: z.number().int().nonnegative() }),
  message: z.string()
})

export type UserStatusRole = z.infer<typeof UserStatusRoleSchema>
export type UserStatusValue = z.infer<typeof UserStatusValueSchema>
export type UserStatusConfigMap = z.infer<typeof UserStatusConfigMapSchema>
export type PlatformManagerSwitchState = z.infer<typeof PlatformManagerSwitchStateSchema>
export type PlatformManagerSwitchName = z.infer<typeof PlatformManagerSwitchNameSchema>
export type GetUserStatusConfigsRequest = z.infer<typeof GetUserStatusConfigsRequestSchema>
export type GetUserStatusConfigsData = z.infer<typeof GetUserStatusConfigsDataSchema>
export type GetUserStatusConfigsResponse = z.infer<typeof GetUserStatusConfigsResponseSchema>
export type UpdateUserStatusConfigsRequest = z.infer<typeof UpdateUserStatusConfigsRequestSchema>
export type UpdateUserStatusConfigsBody = z.infer<typeof UpdateUserStatusConfigsBodySchema>
export type UpdateUserStatusConfigsResponse = z.infer<typeof UpdateUserStatusConfigsResponseSchema>
export type WaitingUserRole = z.infer<typeof WaitingUserRoleSchema>
export type WaitingUserCounts = z.infer<typeof WaitingUserCountsSchema>
export type GetWaitingUserCountsRequest = z.infer<typeof GetWaitingUserCountsRequestSchema>
export type GetWaitingUserCountsResponse = z.infer<typeof GetWaitingUserCountsResponseSchema>
export type ActivateWaitingUsersRequest = z.infer<typeof ActivateWaitingUsersRequestSchema>
export type ActivateWaitingUsersBody = z.infer<typeof ActivateWaitingUsersBodySchema>
export type ActivateWaitingUsersResponse = z.infer<typeof ActivateWaitingUsersResponseSchema>
